import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Upload, Trash2, ImageIcon, Loader2, Plus, Pencil, X,
  Youtube, Globe, LayoutGrid, GripVertical, Eye, EyeOff,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useHotelFeatures } from '../context/HotelFeaturesContext';
import {
  getExploreContent,
  saveExploreContent,
  addExploreActivity,
  updateExploreActivity,
  deleteExploreActivity,
  uploadExploreImages,
  uploadExploreActivityImage,
  deleteExploreImage,
} from '../services/adminService';

const EMPTY_ACTIVITY = { title: '', description: '', imageUrl: '', sortOrder: 0 };

const ExplorePage = () => {
  const { toast } = useToast();
  const { isExploreEnabled, featuresLoading } = useHotelFeatures();

  if (!featuresLoading && !isExploreEnabled) return <Navigate to="/dashboard" replace />;

  // Page-level state
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [pageTitle, setPageTitle] = useState('Explore Our Resort');
  const [subTitle, setSubTitle] = useState('');
  const [introText, setIntroText] = useState('');
  const [youtubeVideoUrl, setYoutubeVideoUrl] = useState('');
  const [isActive, setIsActive] = useState(false);

  // Activities state
  const [activities, setActivities] = useState([]);
  const [activityDialog, setActivityDialog] = useState(null); // null | { mode: 'add'|'edit', data, id? }
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY);
  const [activityImageFile, setActivityImageFile] = useState(null);
  const [activityImagePreview, setActivityImagePreview] = useState(null);
  const [savingActivity, setSavingActivity] = useState(false);
  const [deletingActivityId, setDeletingActivityId] = useState(null);

  // Gallery images state
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const fileInputRef = useRef(null);
  const activityImgRef = useRef(null);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getExploreContent();
      setContent(data);
      setPageTitle(data.pageTitle || 'Explore Our Resort');
      setSubTitle(data.subTitle || '');
      setIntroText(data.introText || '');
      setYoutubeVideoUrl(data.youtubeVideoUrl || '');
      setIsActive(data.isActive || false);
      setActivities(data.activities || []);
      setGalleryImages(data.galleryImages || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load explore content.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // ── Page settings save ──────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await saveExploreContent({ pageTitle, subTitle, introText, youtubeVideoUrl, isActive });
      toast({ title: 'Saved', description: 'Explore page settings updated.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Activity dialog ─────────────────────────────────────────────────────────
  const openAddActivity = () => {
    setActivityForm({ ...EMPTY_ACTIVITY, sortOrder: activities.length });
    setActivityImageFile(null);
    setActivityImagePreview(null);
    setActivityDialog({ mode: 'add' });
  };

  const openEditActivity = (act) => {
    setActivityForm({ title: act.title, description: act.description || '', imageUrl: act.imageUrl || '', sortOrder: act.sortOrder });
    setActivityImageFile(null);
    setActivityImagePreview(act.imageUrl || null);
    setActivityDialog({ mode: 'edit', id: act.id });
  };

  const closeActivityDialog = () => {
    setActivityDialog(null);
    if (activityImagePreview && activityImageFile) URL.revokeObjectURL(activityImagePreview);
    setActivityImageFile(null);
    setActivityImagePreview(null);
  };

  const handleActivityImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (activityImageFile) URL.revokeObjectURL(activityImagePreview);
    setActivityImageFile(file);
    setActivityImagePreview(URL.createObjectURL(file));
    setActivityForm(f => ({ ...f, imageUrl: '' }));
  };

  const handleSaveActivity = async () => {
    if (!activityForm.title.trim()) {
      toast({ title: 'Validation', description: 'Activity title is required.', variant: 'destructive' });
      return;
    }
    setSavingActivity(true);
    try {
      let imageUrl = activityForm.imageUrl;
      if (activityImageFile) {
        imageUrl = await uploadExploreActivityImage(activityImageFile);
      }
      const payload = { ...activityForm, imageUrl };

      if (activityDialog.mode === 'add') {
        await addExploreActivity(payload);
        toast({ title: 'Added', description: 'Activity added successfully.' });
      } else {
        await updateExploreActivity(activityDialog.id, payload);
        toast({ title: 'Updated', description: 'Activity updated.' });
      }
      closeActivityDialog();
      fetchContent();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingActivity(false);
    }
  };

  const handleDeleteActivity = async (id) => {
    setDeletingActivityId(id);
    try {
      await deleteExploreActivity(id);
      setActivities(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Deleted', description: 'Activity removed.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingActivityId(null);
    }
  };

  const moveActivity = async (index, dir) => {
    const newArr = [...activities];
    const swapIdx = index + dir;
    if (swapIdx < 0 || swapIdx >= newArr.length) return;
    [newArr[index], newArr[swapIdx]] = [newArr[swapIdx], newArr[index]];
    // update sortOrder
    const updated = newArr.map((a, i) => ({ ...a, sortOrder: i }));
    setActivities(updated);
    try {
      await Promise.all(updated.map(a => updateExploreActivity(a.id, { title: a.title, description: a.description, imageUrl: a.imageUrl, sortOrder: a.sortOrder })));
    } catch {
      fetchContent();
    }
  };

  // ── Gallery upload ──────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    setSelectedFiles(files);
    setPreviews(files.map(f => ({ name: f.name, url: URL.createObjectURL(f) })));
  };

  const clearGallerySelection = () => {
    previews.forEach(p => URL.revokeObjectURL(p.url));
    setSelectedFiles([]);
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGalleryUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    try {
      await uploadExploreImages(selectedFiles);
      toast({ title: 'Uploaded', description: `${selectedFiles.length} image(s) added to explore gallery.` });
      clearGallerySelection();
      fetchContent();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (id) => {
    setDeletingImageId(id);
    try {
      await deleteExploreImage(id);
      setGalleryImages(prev => prev.filter(img => img.id !== id));
      toast({ title: 'Deleted', description: 'Image removed.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingImageId(null);
    }
  };

  // ── YouTube embed helper ────────────────────────────────────────────────────
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loader2 className="h-10 w-10 text-blue-400 animate-spin" />
      </div>
    );
  }


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Globe className="h-7 w-7 text-emerald-400" />
            Explore Page
          </h1>
          <p className="text-slate-400 mt-1">Manage your resort's explore / discovery page content</p>
        </div>
        {(
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/80 rounded-lg border border-slate-700">
            <span className="text-sm text-slate-400">Page Status</span>
            <Switch
              checked={isActive}
              onCheckedChange={(val) => setIsActive(val)}
              className="data-[state=checked]:bg-emerald-500"
            />
            <span className={`text-sm font-semibold ${isActive ? 'text-emerald-400' : 'text-slate-500'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        )}
      </div>

      {/* Page Settings Card */}
      <Card className={"bg-slate-800/50 border-slate-700"}>
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-blue-400" />
            Page Settings
          </CardTitle>
          <CardDescription className="text-slate-400">Title, subtitle and introduction text shown on the explore page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-300">Page Title <span className="text-red-400">*</span></Label>
              <Input
                value={pageTitle}
                onChange={e => setPageTitle(e.target.value)}
                placeholder="Explore Our Resort"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Subtitle</Label>
              <Input
                value={subTitle}
                onChange={e => setSubTitle(e.target.value)}
                placeholder="Discover the magic around you"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Introduction Text</Label>
            <Textarea
              value={introText}
              onChange={e => setIntroText(e.target.value)}
              placeholder="Write a welcoming introduction for your explore page..."
              rows={4}
              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 transition-colors resize-none"
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700 min-w-28">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* YouTube Video Card */}
      <Card className={"bg-slate-800/50 border-slate-700"}>
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2">
            <Youtube className="h-5 w-5 text-red-400" />
            Featured Video
          </CardTitle>
          <CardDescription className="text-slate-400">Paste a YouTube URL — it will be embedded on the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-300">YouTube URL</Label>
            <Input
              value={youtubeVideoUrl}
              onChange={e => setYoutubeVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500 transition-colors"
            />
          </div>
          {getYoutubeEmbedUrl(youtubeVideoUrl) && (
            <div className="rounded-xl overflow-hidden border border-slate-700 bg-black aspect-video w-full max-w-2xl">
              <iframe
                src={getYoutubeEmbedUrl(youtubeVideoUrl)}
                title="YouTube preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700 min-w-28">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Video'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activities Card */}
      <Card className={"bg-slate-800/50 border-slate-700"}>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-white">Activities</CardTitle>
              <CardDescription className="text-slate-400 mt-1">What guests can do — each card shows on the explore page</CardDescription>
            </div>
            <Button onClick={openAddActivity} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
              <Plus className="h-4 w-4 mr-2" />Add Activity
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <div className="text-center py-14 border-2 border-dashed border-slate-700 rounded-xl">
              <LayoutGrid className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">No activities yet</p>
              <p className="text-slate-500 text-sm mt-1">Click "Add Activity" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((act, idx) => (
                <div key={act.id} className="flex items-center gap-3 p-3 bg-slate-700/40 rounded-xl border border-slate-600/50 hover:border-slate-500 transition-colors group">
                  {/* Reorder arrows */}
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button
                      onClick={() => moveActivity(idx, -1)}
                      disabled={idx === 0}
                      className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-20 transition-colors"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveActivity(idx, 1)}
                      disabled={idx === activities.length - 1}
                      className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-20 transition-colors"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Activity image */}
                  {act.imageUrl ? (
                    <img src={act.imageUrl} alt={act.title} className="w-14 h-14 object-cover rounded-lg shrink-0" />
                  ) : (
                    <div className="w-14 h-14 bg-slate-600/50 rounded-lg flex items-center justify-center shrink-0">
                      <ImageIcon className="h-6 w-6 text-slate-500" />
                    </div>
                  )}

                  {/* Activity info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{act.title}</p>
                    {act.description && (
                      <p className="text-slate-400 text-sm truncate mt-0.5">{act.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline"
                      onClick={() => openEditActivity(act)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white transition-all">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline"
                      disabled={deletingActivityId === act.id}
                      onClick={() => handleDeleteActivity(act.id)}
                      className="border-red-800/50 text-red-400 hover:bg-red-600/20 hover:border-red-600 transition-all">
                      {deletingActivityId === act.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Explore Gallery Card */}
      <Card className={"bg-slate-800/50 border-slate-700"}>
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-purple-400" />
            Explore Gallery
          </CardTitle>
          <CardDescription className="text-slate-400">Scenic photos displayed at the bottom of the explore page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-500/5 transition-all duration-200"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-300 font-medium">Click to select photos</p>
            <p className="text-slate-500 text-sm mt-1">JPG, PNG, WEBP — auto-converted to WebP</p>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          </div>

          {/* Selected previews */}
          {previews.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                {previews.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p.url} alt={p.name} className="h-20 w-20 object-cover rounded-lg border border-slate-600" />
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(p.url);
                        setSelectedFiles(selectedFiles.filter((_, idx) => idx !== i));
                        setPreviews(previews.filter((_, idx) => idx !== i));
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleGalleryUpload} disabled={uploading} className="bg-purple-600 hover:bg-purple-700">
                  {uploading
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                    : <><Upload className="h-4 w-4 mr-2" />Upload {selectedFiles.length} photo{selectedFiles.length > 1 ? 's' : ''}</>}
                </Button>
                <Button variant="outline" onClick={clearGallerySelection} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Gallery grid */}
          {galleryImages.length === 0 && previews.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No gallery photos yet</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {galleryImages.map((img) => (
                <div key={img.id} className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-800/50 aspect-square">
                  <img src={img.url} alt={img.fileName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button size="sm" variant="outline"
                      disabled={deletingImageId === img.id}
                      onClick={() => handleDeleteImage(img.id)}
                      className="border-red-500/60 text-red-400 hover:bg-red-500/20">
                      {deletingImageId === img.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Add/Edit Dialog */}
      {activityDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeActivityDialog}>
          <div
            className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">
                {activityDialog.mode === 'add' ? 'Add Activity' : 'Edit Activity'}
              </h2>
              <button onClick={closeActivityDialog} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Image upload */}
              <div className="space-y-3">
                <Label className="text-slate-300">Activity Image</Label>
                <div
                  className="relative group cursor-pointer rounded-xl overflow-hidden border-2 border-dashed border-slate-600 hover:border-blue-500 transition-colors"
                  onClick={() => activityImgRef.current?.click()}
                >
                  {activityImagePreview ? (
                    <div className="relative">
                      <img src={activityImagePreview} alt="preview" className="w-full h-44 object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-white text-sm font-medium">
                          Change Image
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-500">
                      <ImageIcon className="h-10 w-10 mb-2" />
                      <p className="text-sm">Click to upload an image</p>
                    </div>
                  )}
                  <input ref={activityImgRef} type="file" accept="image/*" className="hidden" onChange={handleActivityImageSelect} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Title <span className="text-red-400">*</span></Label>
                <Input
                  value={activityForm.title}
                  onChange={e => setActivityForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Morning Yoga, Sunset Cruise"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={activityForm.description}
                  onChange={e => setActivityForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe this activity..."
                  rows={3}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Sort Order</Label>
                <Input
                  type="number"
                  value={activityForm.sortOrder}
                  onChange={e => setActivityForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-700/50 border-slate-600 text-white focus:border-blue-500 w-24"
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 pt-0">
              <Button onClick={handleSaveActivity} disabled={savingActivity} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {savingActivity
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  : activityDialog.mode === 'add' ? 'Add Activity' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={closeActivityDialog} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
